import { makeAutoObservable } from 'mobx';
import { token } from '@/infrastructure/container';
import { type IAppBus } from '@/services/AppBus';
import { type IDomTheme } from '@/services/DomTheme';
import { type ILocalStorage } from '@/services/LocalStorage';

const STORAGE_KEY = 'theme';

export class ThemeStore {
  private _theme: 'light' | 'dark' = 'light';

  constructor(
    private bus: IAppBus,
    private storage: ILocalStorage,
    private domTheme: IDomTheme,
  ) {
    makeAutoObservable(this);
    this.hydrate();
    bus.subscribe('KEY_DOWN', this.onKeyDown);
  }

  get theme(): 'light' | 'dark' {
    return this._theme;
  }

  get isDark(): boolean {
    return this._theme === 'dark';
  }

  toggle = (): void => {
    this.setTheme(this._theme === 'dark' ? 'light' : 'dark');
  };

  setTheme = (theme: 'light' | 'dark'): void => {
    this._theme = theme;
    this.domTheme.applyDarkClass(theme === 'dark');
    this.storage.set(STORAGE_KEY, theme);
    this.bus.themeChanged(theme);
  };

  private hydrate(): void {
    const stored = this.storage.get(STORAGE_KEY);
    const initial: 'light' | 'dark' =
      stored === 'dark' || stored === 'light'
        ? stored
        : this.domTheme.systemPrefersDark()
          ? 'dark'
          : 'light';
    this.setTheme(initial);
  }

  private onKeyDown = (data: { key: string; ctrl: boolean; meta: boolean }): void => {
    if (data.key === 'd' && !data.ctrl && !data.meta) {
      this.toggle();
    }
  };
}

export const TOKEN_ThemeStore = token<ThemeStore>('ThemeStore');
